import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPartners, deletePartner } from '../../redux/features/partnerSlice';
import { getJobs } from '../../redux/features/jobSlice';
import PartnerForm from '../forms/PartnerForm';
import { getPartnerHandoffSummary } from '../../utils/handoffWorkflowUtils';
import { getBusinessType } from '../../utils/businessTypeUtils';
import { UserPlus, Pencil, Trash2, Mail, Phone, Percent, MapPin, DollarSign, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { canAddPartner, getMaxPartners, isFeatureAllowed } from '../../utils/packageLimits';

const PartnerList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState('month'); // 'week', 'month', 'quarter'
  
  const dispatch = useDispatch();
  const { partners, loading, error } = useSelector((state) => state.partner);
  const { jobs } = useSelector((state) => state.job || { jobs: [] });
  const { user } = useSelector((state) => state.auth || {});

  const currentCompanyId = useMemo(
    () => user?.result?._id || user?.result?.companyId || null,
    [user]
  );

  const partnerHandoffSummaryById = useMemo(() => {
    const map = new Map();
    if (!currentCompanyId || !Array.isArray(partners)) return map;

    partners.forEach((partner) => {
      const id = String(partner._id || partner.id || '');
      map.set(id, getPartnerHandoffSummary(partner, jobs, currentCompanyId));
    });
    return map;
  }, [partners, jobs, currentCompanyId]);

  const getPartnerInitials = (partner) => {
    const name = String(partner?.companyName || partner?.name || '').trim();
    return name ? name.substring(0, 2).toUpperCase() : '??';
  };
  
  useEffect(() => {
    dispatch(getPartners());
    const businessType = user?.result?.businessType || getBusinessType();
    if (businessType) {
      dispatch(getJobs({ businessType, fetchAll: true }));
    } else {
      dispatch(getJobs());
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);
  
  // Calculate date range based on period
  const getDateRange = (period) => {
    const now = new Date();
    const start = new Date();
    
    switch (period) {
      case 'week':
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(now.getMonth() - 3);
        break;
      default:
        start.setMonth(now.getMonth() - 1);
    }
    return { start, end: now };
  };
  
  // Calculate partner statistics
  const calculatePartnerStats = (partner) => {
    const { start, end } = getDateRange(statsPeriod);
    const partnerIdStr = String(partner._id);
    
    // Get all assigned jobs for this partner
    const allAssignedJobs = jobs?.filter(job => {
      if (!job?.assignedTo || job.assignedTo === 'Unassigned' || job.assignedTo === '') {
        return false;
      }
      
      // Handle populated object from server
      if (typeof job.assignedTo === 'object' && job.assignedTo !== null) {
        const assignedToId = String(job.assignedTo._id || job.assignedTo.id || '');
        return assignedToId === partnerIdStr;
      }
      
      // Handle string ID (with or without prefix)
      const assignedToStr = String(job.assignedTo);
      
      // Direct ID match
      if (assignedToStr === partnerIdStr) {
        return true;
      }
      
      // Check if assignedTo has "partner:" prefix
      if (assignedToStr.startsWith('partner:')) {
        const partnerIdFromAssigned = assignedToStr.replace('partner:', '');
        return partnerIdFromAssigned === partnerIdStr;
      }
      
      // Skip if it's a worker
      if (assignedToStr.startsWith('worker:')) {
        return false;
      }
      
      return false;
    }) || [];
    
    // Get completed jobs (all time, not filtered by period)
    const completedJobs = allAssignedJobs.filter(job => 
      job.status === 'Completed'
    );
    
    // Filter completed jobs within the period for revenue stats
    const partnerJobs = completedJobs.filter(job => {
      if (!job.report) return false;
      const completedDate = new Date(job.report.submittedAt || job.updatedAt);
      return completedDate >= start && completedDate <= end;
    });
    
    // Calculate statistics
    let totalRevenue = 0; // Total revenue from completed jobs
    let totalProfit = 0; // Total profit (revenue - costs)
    let partnerShare = 0; // Partner's share based on commissionRate
    
    partnerJobs.forEach(job => {
      if (job.report) {
        // Parse revenue
        const revenueStr = job.report.serviceCharged || '0';
        const revenue = parseFloat(revenueStr.toString().replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        totalRevenue += revenue;
        
        // Calculate profit (revenue - costs)
        const reversiCost = job.report.reversiCost || 0;
        const additionalMaterialsCost = job.report.additionalMaterialsCost || 0;
        const amortizationCost = job.report.amortizationCost || 0;
        const totalCost = reversiCost + additionalMaterialsCost + amortizationCost;
        const profit = revenue - totalCost;
        totalProfit += profit;
        
        // Calculate partner's share based on commissionRate
        // Partner gets commissionRate% of the profit (not revenue)
        const commissionRate = partner.commissionRate || 0;
        const partnerCommission = (profit * commissionRate) / 100;
        partnerShare += partnerCommission;
      }
    });
    
    // Format time helper
    const formatTime = (seconds) => {
      if (!seconds || seconds === 0) return '0h 0m';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    };
    
    return {
      totalJobs: allAssignedJobs.length,
      completedJobs: completedJobs.length,
      totalRevenue,
      totalProfit,
      partnerShare, // Partner's commission based on profit
      companyShare: totalProfit - partnerShare // Company's remaining share
    };
  };
  
  // Check if add button should be disabled
  const currentPartnerCount = partners?.length || 0;
  const canAdd = canAddPartner(user, currentPartnerCount) && isFeatureAllowed('partners', user);
  const maxPartners = getMaxPartners(user);
  
  const handleAddPartner = () => {
    // Check if feature is allowed
    if (!isFeatureAllowed('partners', user)) {
      toast.error('Partneri nisu dostupni u vašem paketu. Nadogradite na Standard paket ili više.');
      return;
    }
    
    // Check if user can add more partners
    if (!canAddPartner(user, currentPartnerCount)) {
      toast.error(`Dostigli ste limit partnera za vaš paket. Maksimalno dozvoljeno: ${maxPartners === Infinity ? 'neograničeno' : maxPartners}. Nadogradite paket za više partnera.`);
      return;
    }
    
    setIsEdit(false);
    setSelectedPartner(null);
    setIsModalOpen(true);
  };
  
  const handleEditPartner = (partner) => {
    setIsEdit(true);
    setSelectedPartner(partner);
    setIsModalOpen(true);
  };
  
  const handleDeletePartner = async (partnerId) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovog partnera?')) {
      try {
        await dispatch(deletePartner({ id: partnerId }));
        toast.success('Partner je uspešno obrisan');
      } catch (error) {
        toast.error('Greška pri brisanju partnera');
      }
    }
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPartner(null);
    setIsEdit(false);
    // Refresh partners list after modal closes
    dispatch(getPartners());
  };

  const getPartnerTypeLabel = (type) => {
    const labels = {
      'subcontractor': 'Podizvođač',
      'supplier': 'Dobavljač',
      'service_provider': 'Servisni partner',
      'other': 'Ostalo'
    };
    return labels[type] || type;
  };
  
  if (loading) {
    return (
      <div className="bg-gray-900 min-h-screen p-2 sm:p-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!partners || partners.length === 0) {
    return (
      <div className="bg-gray-900 min-h-screen p-2 sm:p-4">
        {/* Header */}
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-xl sm:text-2xl font-bold text-white">Partneri</h2>
            <button
              onClick={handleAddPartner}
              disabled={!canAdd}
              className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 text-sm rounded-lg transition-colors ${
                canAdd
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
              }`}
              title={!canAdd ? (isFeatureAllowed('partners', user) ? `Limit partnera: ${maxPartners === Infinity ? 'neograničeno' : maxPartners}` : 'Partneri nisu dostupni u vašem paketu') : ''}
            >
              <UserPlus size={18} /> 
              <span className="hidden sm:inline">Dodaj partnera</span>
              <span className="sm:hidden">Dodaj</span>
            </button>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-6 sm:p-8 text-center text-gray-400 shadow-lg">
          Nema partnera. Dodajte prvog partnera da počnete.
        </div>
        
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2 sm:p-4">
            <div className="bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <PartnerForm
                isEdit={isEdit}
                partner={selectedPartner}
                onClose={handleCloseModal}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="bg-gray-900 p-2 sm:p-4">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-white">Partneri</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Period Selector */}
            <select
              value={statsPeriod}
              onChange={(e) => setStatsPeriod(e.target.value)}
              className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="week">Nedelja</option>
              <option value="month">Mesec</option>
              <option value="quarter">Kvartal</option>
            </select>
            <button
              onClick={handleAddPartner}
              disabled={!canAdd}
              className={`flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 text-sm rounded-lg transition-colors ${
                canAdd
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
              }`}
              title={!canAdd ? (isFeatureAllowed('partners', user) ? `Limit partnera: ${maxPartners === Infinity ? 'neograničeno' : maxPartners}` : 'Partneri nisu dostupni u vašem paketu') : ''}
            >
              <UserPlus size={18} /> 
              <span className="hidden sm:inline">Dodaj partnera</span>
              <span className="sm:hidden">Dodaj</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Partner Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {partners.map((partner) => {
          const stats = calculatePartnerStats(partner);
          const handoffSummary =
            partnerHandoffSummaryById.get(String(partner._id || partner.id || '')) || {
              forwardedCount: 0,
              closedByPartnerCount: 0
            };
          
          return (
            <div key={partner._id} className="bg-gray-800 p-3 sm:p-4 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex justify-between items-start mb-2 sm:mb-3">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base flex-shrink-0 shadow-sm"
                    style={{
                      backgroundColor: partner.color || '#F97316',
                      background: partner.color || '#F97316'
                    }}
                    title={partner.companyName}
                  >
                    {getPartnerInitials(partner)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm sm:text-base text-white truncate">
                      {partner.companyName}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 truncate">
                      {getPartnerTypeLabel(partner.partnerType)}
                    </p>
                    {(handoffSummary.forwardedCount > 0 ||
                      handoffSummary.closedByPartnerCount > 0) && (
                      <p className="text-[11px] sm:text-xs text-gray-400 mt-0.5">
                        Prosleđeno: {handoffSummary.forwardedCount} · Zatvoreno od
                        partnera: {handoffSummary.closedByPartnerCount}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-2">
                  <button
                    onClick={() => handleEditPartner(partner)}
                    className="p-1.5 sm:p-2 text-blue-400 hover:text-blue-300 hover:bg-gray-700 rounded transition-colors"
                    title="Izmeni partnera"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDeletePartner(partner._id)}
                    className="p-1.5 sm:p-2 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded transition-colors"
                    title="Obriši partnera"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              
              {/* Statistics */}
              <div className="text-xs sm:text-sm space-y-2 border-t border-gray-700 pt-2 sm:pt-3">
                {/* Basic Info - All in one row */}
                <div className="flex items-center gap-2 text-gray-300 flex-wrap">
                  {partner.contactPerson && (
                    <span className="flex items-center gap-1" title={partner.contactPerson}>
                      👤 {partner.contactPerson}
                    </span>
                  )}
                  {partner.contactPerson && (partner.email || partner.phone) && (
                    <span className="text-gray-500">•</span>
                  )}
                  {partner.email && (
                    <span className="flex items-center gap-1 truncate" title={partner.email}>
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{partner.email}</span>
                    </span>
                  )}
                  {partner.email && partner.phone && (
                    <span className="text-gray-500">•</span>
                  )}
                  {partner.phone && (
                    <span className="flex items-center gap-1 truncate" title={partner.phone}>
                      <Phone className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{partner.phone}</span>
                    </span>
                  )}
                </div>
                
                {/* Commission Rate - New row */}
                <div className="pt-2 border-t border-gray-700">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Percent className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-gray-400 text-xs">Procenat provizije</span>
                    </div>
                    <span className="text-white font-medium text-sm">{partner.commissionRate}%</span>
                  </div>
                </div>
                
                {/* Job Statistics */}
                <div className="pt-2 border-t border-gray-700 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Završeni poslovi:
                    </span>
                    <span className="text-white font-medium text-sm">{stats.completedJobs}</span>
                  </div>
                </div>
                
                {/* Revenue and Profit */}
                <div className="pt-2 border-t border-gray-700 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Ukupan prihod:
                    </span>
                    <span className="text-white font-medium text-sm">
                      {stats.totalRevenue.toLocaleString('sr-RS')} RSD
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs">Profit:</span>
                    <span className="text-white font-medium text-sm">
                      {stats.totalProfit.toLocaleString('sr-RS')} RSD
                    </span>
                  </div>
                </div>
                
                {/* Profit Split */}
                <div className="pt-2 border-t border-gray-700 space-y-2 bg-gray-700/30 rounded p-2">
                  <div className="text-gray-400 text-xs font-semibold mb-1">Podela zarade:</div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300 text-xs">Partner ({partner.commissionRate}%):</span>
                    <span className="text-green-400 font-medium text-sm">
                      {stats.partnerShare.toLocaleString('sr-RS')} RSD
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300 text-xs">Kompanija:</span>
                    <span className="text-blue-400 font-medium text-sm">
                      {stats.companyShare.toLocaleString('sr-RS')} RSD
                    </span>
                  </div>
                </div>
                
                {/* Address */}
                {partner.address && (partner.address.street || partner.address.city) && (
                  <div className="pt-2 border-t border-gray-700">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-blue-400 flex-shrink-0" />
                      <p className="text-gray-300 text-xs truncate">
                        {[partner.address.street, partner.address.city].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Status */}
                <div className="pt-2 border-t border-gray-700">
                  <span className={`px-2 py-1 rounded text-xs ${partner.active ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'}`}>
                    {partner.active ? 'Aktivan' : 'Neaktivan'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2 sm:p-4">
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <PartnerForm
              isEdit={isEdit}
              partner={selectedPartner}
              onClose={handleCloseModal}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerList;
